import type { FC } from 'react';

import { Search } from '@coop/components';
import { useEffect, useRef } from 'react';

import type { GlobalSearchResults } from '../../../search/models/models';

import { useSearchOverlayGA4ActionTracking } from '../../../common/tracking/hooks/useSearchOverlayGA4Tracking';
import { useSearchStateContext } from '../../../search/contexts/searchStateContext';
import { getFullSearchResultUrlWithOrder } from '../../../search/utils/searchResultUtils';

interface GlobalSearchBarProps {
    performFullSearch?: (newQuery: string, originalQuery?: string) => void;
}

const GlobalSearchBar: FC<React.PropsWithChildren<GlobalSearchBarProps>> = (props) => {
    const searchRef = useRef<HTMLInputElement>(null);
    const {
        result,
        query,
        close,
        show,
        isLoading,
        clear,
        open,
        setQuery,
        expectedSearchResultType,
        searchFromPage,
    } = useSearchStateContext<GlobalSearchResults>();

    const { trackEnterAction } = useSearchOverlayGA4ActionTracking(
        query,
        result?.queryUsed,
        searchFromPage,
        isLoading,
    );

    const navigateToGlobalSearchResultsPage = () => {
        trackEnterAction();
        searchRef.current?.blur();
        let realQuery = isLoading ? query : result?.queryUsed;
        if (!realQuery) {
            realQuery = '';
        }
        if (props.performFullSearch) {
            close();
            props.performFullSearch(realQuery, query);
            return;
        }

        const viewAllResultsUrl = getFullSearchResultUrlWithOrder(
            realQuery,
            query,
            expectedSearchResultType,
        ).path;

        window.location.href = viewAllResultsUrl;
    };

    useEffect(() => {
        if (show) {
            open();
        }
    }, [show, open]);

    return (
        <Search
            inputRef={searchRef}
            submitButtonLabel="Sök på Coop"
            value={query}
            onChange={setQuery}
            placeholder="Sök recept, varor, butiker och artiklar"
            aria-label="Sökresultaten kommer att visas medan du skriver"
            onEnterPress={navigateToGlobalSearchResultsPage}
            onEscapePress={() => {
                clear();
                searchRef.current?.blur();
            }}
            onClear={clear}
            aria-haspopup="dialog"
        />
    );
};

export default GlobalSearchBar;
